<script setup lang="ts">
import { Dialog } from "../../../../base-components/Headless";
import { FormLabel, FormInput } from "../../../../base-components/Form";
import Button from "../../../../base-components/Button";
import LoadingIcon from "../../../../base-components/LoadingIcon";
import Lucide from "../../../../base-components/Lucide/Lucide.vue";
import { ref } from "vue";
import { useSectoresStore } from "../../../../services/sectores";
import { useMensajesStore } from "../../../../services/mensajes";
import type { Sector } from "../../../../services/sectores";
import { useMesasStore } from "../../../../services/mesas";

const emit = defineEmits<{ (e: "cerrar"): void }>();
const sectoresStore = useSectoresStore();
const mensajesStore = useMensajesStore();
const mesasStore = useMesasStore();

const nombreNuevo = ref("");
const cargando = ref(false);
const sectorPendienteEliminar = ref<Sector | null>(null);

const agregar = async () => {
  const nombre = nombreNuevo.value.trim();
  if (!nombre) return;
  cargando.value = true;
  try {
    const ok = await sectoresStore.crear(nombre);
    if (ok) {
      nombreNuevo.value = "";
      mensajesStore.mostrarToast(
        "Sector creado",
        `Se creó el sector "${nombre}".`,
        mensajesStore.mensajeType.exito
      );
    } else {
      mensajesStore.mostrarModal(
        "Error",
        "No se pudo crear el sector. Intentá de nuevo.",
        mensajesStore.mensajeType.error
      );
    }
  } finally {
    cargando.value = false;
  }
};

const solicitarEliminar = (s: Sector) => {
  const general = sectoresStore.sectores.find(
    sec => (sec.nombre ?? "").trim().toLowerCase() === "general"
  );

  if (general?.id === s.id) {
    mensajesStore.mostrarModal(
      "No se puede eliminar",
      'El sector "General" es requerido.',
      mensajesStore.mensajeType.error
    );
    return;
  }

  if (!s.id) {
    return;
  }

  sectorPendienteEliminar.value = s;
};

const confirmarEliminar = async () => {
  if (!sectorPendienteEliminar.value?.id) return;

  const sectorAEliminar = sectorPendienteEliminar.value;
  cargando.value = true;
  try {
    const ok = await sectoresStore.eliminar(sectorAEliminar.id);
    if (ok) {
      mensajesStore.mostrarToast(
        "Sector eliminado",
        `Se eliminó el sector "${sectorAEliminar.nombre}".`,
        mensajesStore.mensajeType.exito
      );
      sectorPendienteEliminar.value = null;
    } else {
      mensajesStore.mostrarModal(
        "Error",
        "No se pudo eliminar el sector. Intentá de nuevo.",
        mensajesStore.mensajeType.error
      );
    }

    await mesasStore.getFromFirestore();
  } finally {
    cargando.value = false;
  }
};
</script>

<template>
  <Dialog :open="true" @close="() => emit('cerrar')" size="md">
    <Dialog.Panel>
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">Gestionar sectores</h2>
      </Dialog.Title>
      <Dialog.Description>
        <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Los sectores permiten tener un mapa por zona (ej. Salón, Terraza). Asigná el sector al crear o editar cada mesa.
        </p>
        <div class="mt-4 flex flex-wrap items-end gap-2">
          <div class="flex-1 min-w-[180px]">
            <FormLabel htmlFor="sector-nombre">Nuevo sector</FormLabel>
            <FormInput
              id="sector-nombre"
              v-model="nombreNuevo"
              type="text"
              placeholder="Ej: Terraza"
              class="mt-1"
              @keydown.enter.prevent="agregar"
            />
          </div>
          <Button variant="primary" :disabled="cargando || !nombreNuevo.trim()" @click="agregar">
            <LoadingIcon v-if="cargando" icon="oval" class="w-4 h-4 mr-1" />
            Agregar
          </Button>
        </div>
        <ul class="mt-4 space-y-2">
          <li
            v-for="s in sectoresStore.sectores"
            :key="s.id"
            class="flex items-center justify-between rounded-lg border border-slate-200 dark:border-darkmode-400 px-3 py-2"
          >
            <span class="font-medium">{{ s.nombre }}</span>
            <Button
              variant="outline-secondary"
              size="sm"
              :disabled="cargando"
              @click="solicitarEliminar(s)"
            >
              <Lucide icon="Trash2" class="w-4 h-4" />
            </Button>
          </li>
          <li v-if="sectoresStore.sectores.length === 0" class="text-sm text-slate-500 py-2">
            No hay sectores. Agregá uno para tener mapas por zona.
          </li>
        </ul>
      </Dialog.Description>
      <Dialog.Footer class="flex-col items-stretch gap-5 mb-5">
        <div
          v-if="sectorPendienteEliminar"
          class="rounded-md border border-danger/30 bg-danger/10 px-3 py-3 text-sm text-danger flex items-center min-h-[52px] mb-5"
        >
          ¿Deseas eliminar el sector "{{ sectorPendienteEliminar.nombre }}"?
        </div>
        <div class="flex items-center justify-end gap-3">
          <Button variant="outline-secondary" @click="() => emit('cerrar')">
            Cerrar
          </Button>
          <Button
            v-if="sectorPendienteEliminar"
            variant="danger"
            :disabled="cargando"
            @click="confirmarEliminar"
          >
            <LoadingIcon v-if="cargando" icon="oval" class="w-4 h-4 mr-1" />
            Confirmar eliminar
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
